/** Images des symboles de mission : copies du catalogue de l'app iPad (tools/assets/copy_mission_symbols.py),
 *  servies en @2x sous /mission-symbols et chargées dans la carte au démarrage. */

import flips from "./missionFlips.json";

export const MISSION_PREFIX = "mission:";

/** Autre variante d'une image et angle de lecture de son texte (tools/assets/sync_server_mission_symbols.py, qui
 *  produit les variantes « …Retourne » et cette table ; le serveur en a une copie identique). */
export interface MissionFlip {
  /** Nom ASCII de l'autre variante : texte d'un demi-tour, flèche dans la même direction. */
  pair: string;
  /** Angle de lecture du texte dans l'image (degrés, sens horaire ; 0 : de gauche à droite). */
  text: number;
  /** « y » : l'autre variante est un miroir haut/bas de celle-ci (JALONNER, FREINER). */
  mirror?: "y";
}

/** Table des retournements, par nom ASCII : images à texte qui peuvent être tournées de plus de 90°. */
export const MISSION_FLIPS: Readonly<Record<string, MissionFlip>> = flips as Record<string, MissionFlip>;

const DIRECTIONAL = [
  "Reco", "EniReco", "App", "EniApp", "Raid", "Atk", "EniAtk", "Catk", "EniCatk", "Det", "Neut", "Expl",
  "S'emparer", "Rri", "Tenir", "TenirAmi", "S'infiltrerAmi", "S'infiltrer", "Eclairer", "ContreRebellion",
];
const FIXED = [
  "Fix0to180", "Fix180To0", "CoupDeMain", "Interdire", "Défendre", "Jalonner", "EniJalonner", "Barrer",
  "Freiner", "EniFreiner", "Relever", "Couvrir", "Boucler", "Surveiller", "EniSurveiller", "Baliser",
  "Brecher", "Pieger", "Ctrz", "Attaquer",
];

/** Noms des images que renvoie le serveur : celles de l'iPad, et leurs copies à la couleur du camp de l'unité
 *  (`EniCatk…`, `EniReco…`, `EniSurveiller`, `TenirAmi…` : `mission_symbols.camp_image`, constat d'Alexis du
 *  15/10/2026 ; tools/assets/sync_server_mission_symbols.py). */
export const MISSION_IMAGES = [...DIRECTIONAL.flatMap((b) => [`${b}0To180`, `${b}180To0`]), ...FIXED];

/** Nom ASCII d'une image, celui de son fichier (« S'emparer0To180 » → « Semparer0To180 », « Défendre » →
 *  « Defendre ») : identifiant de l'image sur la carte et clé de la table des retournements. */
export function missionStem(name: string): string {
  return name.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/[^A-Za-z0-9]/g, "");
}

/** Fichier d'une image (nom de l'iPad ou nom ASCII). */
export function missionFile(name: string): string {
  return `/mission-symbols/${missionStem(name)}@2x.png`;
}

interface ImageHost {
  hasImage(id: string): boolean;
  addImage(id: string, image: HTMLImageElement, options?: { pixelRatio?: number }): void;
}

/** Charge toutes les images (à l'échelle @2x : 1 point iPad = 1 pixel CSS), sous leur nom ASCII, variantes
 *  retournées comprises (`missionFlips.json`, `shownMissionImage`) : toutes sont prêtes avant qu'un changement
 *  d'orientation de la carte n'en demande une, la bascule se fait donc sans image manquante. */
export async function loadMissionImages(map: ImageHost): Promise<void> {
  const stems = new Set([...MISSION_IMAGES, ...Object.keys(MISSION_FLIPS)].map(missionStem));
  await Promise.all(
    [...stems].map(async (stem) => {
      const id = `${MISSION_PREFIX}${stem}`;
      if (map.hasImage(id)) return;
      const img = new Image();
      img.src = missionFile(stem);
      await img.decode();
      if (!map.hasImage(id)) map.addImage(id, img, { pixelRatio: 2 });
    }),
  );
}

/** Image « Neutre » du serveur : verbe de section ou de peloton sans image du catalogue (niveau compagnie, docs/21
 *  §5 : NETTOYER, RECUEILLIR, FOUILLER, ESCORTER, PROTÉGER, CONTRÔLER). La carte dessine à sa place une pastille
 *  neutre qui porte le verbe, dans la langue de l'interface (fiche §8). */
export const NEUTRAL_IMAGE = "Neutre";
const NEUTRAL_PREFIX = `${MISSION_PREFIX}${NEUTRAL_IMAGE}:`;

/** Verbes courts de la pastille en anglais (tâches OTAN ; RPOL : sigle retenu au lexique, registre A70). */
const CHIP_EN: Record<string, string> = {
  NETTOYER: "CLEAR",
  RECUEILLIR: "RPOL",
  FOUILLER: "SEARCH",
  ESCORTER: "ESCORT",
  PROTEGER: "PROTECT",
  CONTROLER: "CONTROL",
  SOUTENIR: "SUSTAIN",
};

const plainKey = (text: string) => text.normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase();

/** Texte de la pastille d'un verbe : le verbe français tel quel, ou sa tâche OTAN en anglais. */
export function neutralChipLabel(mission: string, language: string): string {
  return language === "en" ? (CHIP_EN[plainKey(mission)] ?? mission.toUpperCase()) : mission.toUpperCase();
}

/** Identifiant de l'image de la pastille (une par langue et par verbe, dessinée à la demande). */
export function neutralIcon(mission: string, language: string): string {
  return `${NEUTRAL_PREFIX}${language}:${mission}`;
}

/** Pastille neutre @2x portant `label` : fond blanc, filet et texte gris ardoise — aucune couleur de camp ni
 *  d'obstacle (APP-6), seulement le verbe. Null sans canevas. */
export function drawNeutralChip(label: string): HTMLCanvasElement | null {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  // Échelle @2x, comme les images du catalogue de l'iPad (1 point = 1 pixel CSS).
  const font = "600 22px 'Instrument Sans Variable', system-ui, sans-serif";
  ctx.font = font;
  const height = 40;
  const width = Math.max(height, Math.ceil(ctx.measureText(label).width) + 32);
  canvas.width = width;
  canvas.height = height;
  ctx.font = font;
  // ui-check: ignore-start — dessin d'une image de la carte (comme symbols.ts) : couleurs du rendu, pas de jetons.
  ctx.fillStyle = "#ffffff";
  ctx.strokeStyle = "#64748b";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.roundRect(1.5, 1.5, width - 3, height - 3, (height - 3) / 2);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = "#1e293b";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, width / 2, height / 2 + 1);
  // ui-check: ignore-end
  return canvas;
}

/** Dessine la pastille demandée par la carte (« styleimagemissing ») ; faux si l'image n'en est pas une. */
export function provideNeutralChip(
  map: { hasImage(id: string): boolean; addImage(id: string, image: ImageData, options?: { pixelRatio?: number }): void },
  id: string,
): boolean {
  if (!id.startsWith(NEUTRAL_PREFIX)) return false;
  if (map.hasImage(id)) return true;
  const rest = id.slice(NEUTRAL_PREFIX.length);
  const cut = rest.indexOf(":");
  const canvas = drawNeutralChip(neutralChipLabel(rest.slice(cut + 1), rest.slice(0, cut)));
  const ctx = canvas?.getContext("2d");
  if (canvas && ctx) map.addImage(id, ctx.getImageData(0, 0, canvas.width, canvas.height), { pixelRatio: 2 });
  return true;
}
